import { UIMessage } from "ai";
import ReactMarkdown from "react-markdown";

import { ArrowReload } from "@/assets/icons";
import {
    AlfredAccountsListCard,
    AlfredAccountsSummaryCard,
    AlfredActionsButtons,
    AlfredCompactTable,
    AlfredInsightCard,
    AlfredReconciliationCard,
    AlfredStatusSummaryCard,
    AlfredTransactionCard,
} from "@/components";
import {
    AlfredAccountDataInterface,
    AlfredAccountListDataInterface,
    AlfredActionInterface,
    AlfredInsightDataInterface,
    AlfredMessageToolPartInterface,
    AlfredReconciliationDataInterface,
    AlfredStatusDataInterface,
    AlfredTableDataInterface,
    AlfredTransactionDataInterface,
} from "@/interfaces";
import { generateValidClassNameHandler } from "@/lib/utils";

const LoadingCard = ({ type } : { type: string }) => (
    <div className="my-2 flex items-center gap-2 rounded-lg border bg-gray-50 p-4">
        <ArrowReload className="size-4 animate-spin text-primary" />
        <span className="text-sm text-gray-600">
            Loading
            {type}
            ...
        </span>
    </div>
);

const getToolName = (part: AlfredMessageToolPartInterface): string | null => {
    // First check for Vercel AI SDK format with toolName property
    if (part.toolName) return part.toolName;

    // Fallback to custom format where name is embedded in type
    if (part.type.startsWith("tool-") && part.type !== "tool-call" && part.type !== "tool-result") return part.type.slice(5);

    return null;
};

/**
 * Check if a part is a tool part (either AI SDK or custom format).
 */
const isToolPart = (part: AlfredMessageToolPartInterface): boolean => {
    // AI SDK format: has toolName property
    if (part.toolName) return true;
    // Custom format: type starts with "tool-" but isn't a standard type
    if (part.type.startsWith("tool-") && part.type !== "tool-call" && part.type !== "tool-result") return true;
    // AI SDK tool-result type
    if (part.type === "tool-result") return true;
    return false;
};

/**
 * Check if a part has output available.
 * The part has a "state" property that can be:
 * - "input-streaming" - tool is receiving input
 * - "input-available" - tool input is ready
 * - "output-available" - tool output is ready
 * - "output-error" - tool encountered an error
 *
 * For tool-result parts, output is always available.
 */
const hasOutputAvailable = (part: AlfredMessageToolPartInterface): boolean => {
    // tool-result type means we have the result
    if (part.type === "tool-result") return true;
    // Standard state check
    return part.state === "output-available";
};

/**
 * Check if a part is still loading (streaming input or pending).
 */
const isLoading = (part: AlfredMessageToolPartInterface): boolean => {
    // tool-call without result means still loading
    if (part.type === "tool-call" && !part.result) return true;
    return part.state === "input-streaming" || part.state === "input-available";
};

/**
 * Get the output/result from a tool part.
 */
const getOutput = (part: AlfredMessageToolPartInterface): unknown => part.output ?? part.result;

/**
 * Renders a rich component based on tool name and output.
 */
const renderToolComponent = (toolName: string, output: unknown, index: number) => {
    switch (toolName) {
    case "display_transaction_card":
    case "transaction-card":
        return (
            <AlfredTransactionCard
                className="my-2"
                data={output as AlfredTransactionDataInterface}
                key={index}
            />
        );

    case "display_account_summary":
    case "account-summary":
    case "accounts-list": {
        // Check if output is multi-account format (has 'accounts' array)
        const accountOutput = output as AlfredAccountListDataInterface | AlfredAccountDataInterface;
        if ("accounts" in accountOutput && Array.isArray(accountOutput.accounts)) {
            return (
                <AlfredAccountsListCard
                    className="my-2"
                    data={accountOutput as AlfredAccountListDataInterface}
                    key={index}
                />
            );
        }
        // Fallback to single account card for backward compatibility
        return (
            <AlfredAccountsSummaryCard
                className="my-2"
                data={accountOutput as AlfredAccountDataInterface}
                key={index}
            />
        );
    }

    case "display_reconciliation_status":
    case "reconciliation-status":
        return (
            <AlfredReconciliationCard
                className="my-2"
                data={output as AlfredReconciliationDataInterface}
                key={index}
            />
        );

    case "display_data_table":
    case "data-table":
        return (
            <AlfredCompactTable
                className="my-2"
                data={output as AlfredTableDataInterface}
                key={index}
            />
        );

    case "display_stats_summary":
    case "stats-summary":
        return (
            <AlfredStatusSummaryCard
                className="my-2"
                data={output as AlfredStatusDataInterface}
                key={index}
            />
        );

    case "display_insight_alert":
    case "insight-alert":
        return (
            <AlfredInsightCard
                className="my-2"
                data={output as AlfredInsightDataInterface}
                key={index}
            />
        );

    case "display_action_buttons":
    case "action-buttons":
        return (
            <AlfredActionsButtons
                actions={output as AlfredActionInterface[]}
                className="my-2"
                key={index}
            />
        );

    default:
        // For unknown tools, try to render as JSON
        if (output && typeof output === "object") {
            return (
                <pre
                    className="my-2 overflow-x-auto rounded bg-gray-100 p-2 text-xs"
                    key={index}
                >
                    {JSON.stringify(
                        output,
                        null,
                        2,
                    )}
                </pre>
            );
        }
        return null;
    }
};

/**
 * Renders a single message part based on its type.
 * Supports text parts and tool parts for rich components.
 */
const renderPart = (part: UIMessage["parts"][number], index: number, isUser: boolean) => {
    // Cast to ToolPart for type checking
    const toolPart = part as unknown as AlfredMessageToolPartInterface;

    // Handle text parts
    if (part.type === "text") {
        if (!(part as { text?: string }).text?.trim()) return null;

        return (
            <div
                key={index}
                className={generateValidClassNameHandler(
                    "prose prose-sm max-w-none",
                    isUser && "prose-invert",
                )}
            >
                <ReactMarkdown>{(part as { text: string }).text}</ReactMarkdown>
            </div>
        );
    }

    // Handle tool parts (AI SDK format or custom format)
    if (isToolPart(toolPart)) {
        const toolName = getToolName(toolPart);
        if (toolName) {
            // Show loading state for pending tools
            if (isLoading(toolPart)) {
                return (
                    <LoadingCard
                        key={index}
                        type={toolName}
                    />
                );
            }

            // Only render result for completed tools
            if (hasOutputAvailable(toolPart)) {
                const output = getOutput(toolPart);
                return renderToolComponent(
                    toolName,
                    output,
                    index,
                );
            }
        }
    }

    return null;
};

/**
 * AlfredMessageRenderer component that handles rendering of rich UI components
 * in Alfred chat messages based on message parts.
 *
 * Supports:
 * - Text parts with markdown rendering
 * - Tool parts for rich components (transaction cards, etc.)
 * - Loading states for pending tool calls
 */
export const AlfredMessageRenderer = ({
    isUser,
    message,
} : {
    isUser: boolean,
    message: UIMessage,
}) => {
    if (!message.parts || message.parts.length === 0) return null;

    return (
        <>
            {message.parts.map((part, index) => renderPart(
                part,
                index,
                isUser,
            ))}
        </>
    );
};
